<script lang="ts">
import { capitalize, defineComponent, PropType } from "vue";
import KlereoSlider from "@/shared/components/KlereoSlider.vue";
import {
  makeMultihourlyExamples,
  recurModes,
  Recurrence,
  timeModes,
} from "@/shared/utils/recurrence";
import { injectPool } from "@/shared/utils/injection";
import { isFirmwareBefore } from "@/shared/utils/firmware";

export default defineComponent({
  ...injectPool,
  components: { KlereoSlider },
  props: {
    value: {
      type: Object as PropType<Recurrence>,
      required: true,
    },
    withTimeMode: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["change"],
  data() {
    return {
      modes: recurModes,
      timeModes,
      recurrence: this.value,
    };
  },
  watch: {
    value() {
      this.recurrence = this.value;
    },
    "recurrence.mode": function () {
      if (
        (this.recurrence.mode === "monthly" ||
          this.recurrence.mode === "bimonthly") &&
        !this.recurrence.params.day
      ) {
        this.recurrence.params.day = 1;
      } else if (
        this.recurrence.mode === "multihourly" &&
        !this.recurrence.params.frequency
      ) {
        this.recurrence.params.frequency = 2;
      }
    },
  },
  methods: {
    capitalize,
    formatTime(numMinutes: number) {
      const hours = Math.floor(numMinutes / 60);
      const minutes = numMinutes % 60;
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    },
    formatDay(day: number) {
      return this.$t(
        [
          "Dimanche",
          "Lundi",
          "Mardi",
          "Mercredi",
          "Jeudi",
          "Vendredi",
          "Samedi",
        ][day]
      );
    },
  },
  computed: {
    shouldHideRecurrence() {
      return isFirmwareBefore(this.pool.tabSW, "200");
    },
    pattern() {
      return this.$t(`Recurrence${capitalize(this.recurrence.mode)}Pattern`, {
        frequency: 2,
        ...this.recurrence.params,
        minutes: (this.recurrence.params.minutes ?? 0)
          .toString()
          .padStart(2, "0"),
        timePattern: this.$t(
          `RecurrenceTimePattern${capitalize(
            this.recurrence.params.timeMode ?? ""
          )}`,
          {
            time: this.formatTime(
              this.recurrence.mode === "bimonthly"
                ? (this.recurrence.params.hour ?? 0) * 60
                : this.recurrence.params.time ?? 0
            ),
          }
        ),
        time: this.formatTime(this.recurrence.params.time ?? 0),
        hour: this.recurrence.params.hour ?? 0,
        dayOfWeek: this.formatDay(this.recurrence.params.dayOfWeek ?? 0),
        dayOfWeek1: this.formatDay(this.recurrence.params.dayOfWeek1 ?? 0),
        dayOfWeek2: this.formatDay(this.recurrence.params.dayOfWeek2 ?? 0),
        day2: this.recurrence.params.day + 15,
        examples: makeMultihourlyExamples(
          this.recurrence.params.frequency ?? 2,
          this.recurrence.params.minutes ?? 0
        ),
      });
    },
  },
});
</script>

<template>
  <div v-if="!shouldHideRecurrence">
    <div class="mb-2">
      <h3 class="font-medium mb-1">{{ $t("RecurrenceTitle") }}</h3>
      <select v-model="recurrence.mode" class="text-black">
        <option v-for="mode in modes" :key="mode" :value="mode">
          {{ $t(`RecurrenceMode${capitalize(mode)}`) }}
        </option>
      </select>
    </div>

    <div v-if="recurrence.mode === 'daily'">
      <select
        v-if="withTimeMode"
        v-model="recurrence.params.timeMode"
        class="text-black"
      >
        <option v-for="mode in timeModes" :value="mode">
          {{ $t(`Recurrence${capitalize(mode)}`) }}
        </option>
      </select>

      <klereo-slider
        v-if="recurrence.params.timeMode === 'fixedTime'"
        class="my-2"
        v-model="recurrence.params.time"
        @change="() => $emit('change', recurrence)"
        :min="0"
        :max="24 * 60 - 15"
        :precision="15"
        :format="formatTime"
      />
    </div>

    <div v-if="recurrence.mode === 'weekly'">
      <select
        v-if="withTimeMode"
        v-model="recurrence.params.timeMode"
        class="text-black mb-2"
      >
        <option v-for="mode in timeModes" :value="mode">
          {{ $t(`Recurrence${capitalize(mode)}`) }}
        </option>
      </select>
      <div
        class="grid items-center gap-1.5"
        style="grid-template-columns: auto 1fr"
      >
        <div class="px-2">{{ $t("RecurrenceLabelOnDay") }}</div>
        <klereo-slider
          v-model="recurrence.params.dayOfWeek"
          class="grow"
          :min="0"
          :max="6"
          :precision="1"
          :format="formatDay"
        />

        <template v-if="recurrence.params.timeMode === 'fixedTime'">
          <div class="px-2">{{ $t("RecurrenceLabelOnTime") }}</div>
          <klereo-slider
            v-model="recurrence.params.time"
            class="grow"
            :min="0"
            :max="24 * 60"
            :precision="60"
            :format="formatTime"
          />
        </template>
      </div>
    </div>

    <div v-if="recurrence.mode === 'monthly'">
      <div
        class="grid items-center gap-1.5"
        style="grid-template-columns: auto 1fr"
      >
        <div class="px-2">{{ $t("RecurrenceLabelOnDay") }}</div>

        <klereo-slider
          v-model="recurrence.params.day"
          :min="1"
          :max="31"
          :precision="1"
        />
        <div class="px-2">{{ $t("RecurrenceLabelOnTime") }}</div>

        <klereo-slider
          v-model="recurrence.params.time"
          :min="0"
          :max="24 * 60 - 15 * 4 * 3"
          :precision="15 * 4 * 3"
          :format="formatTime"
        />
      </div>
    </div>

    <div v-if="recurrence.mode === 'hourly'">
      <klereo-slider
        v-model="recurrence.params.minutes"
        :min="0"
        :max="59"
        :precision="1"
      />
    </div>

    <div
      class="grid items-center gap-1.5"
      v-if="recurrence.mode === 'multihourly'"
    >
      <klereo-slider
        v-model="recurrence.params.frequency"
        :min="2"
        :max="17"
        :precision="1"
      />
      <klereo-slider
        v-model="recurrence.params.minutes"
        :min="0"
        :max="55"
        :precision="5"
      />
    </div>

    <div v-if="recurrence.mode === 'biweekly'">
      <div
        class="grid items-center gap-1.5"
        style="grid-template-columns: auto 1fr"
      >
        <div class="px-2">{{ $t("RecurrenceLabelOnDay") }}</div>

        <klereo-slider
          v-model="recurrence.params.dayOfWeek1"
          :min="0"
          :max="6"
          :precision="1"
          :format="formatDay"
        />
        <div class="px-2">{{ $t("RecurrenceLabelAnd") }}</div>

        <klereo-slider
          v-model="recurrence!.params!.dayOfWeek2"
          :min="0"
          :max="6"
          :precision="1"
          :format="formatDay"
        />
        <div class="px-2">{{ $t("RecurrenceLabelOnTime") }}</div>

        <klereo-slider
          v-model="recurrence!.params.hour"
          :min="0"
          :max="18"
          :precision="6"
          :format="(h) => formatTime(h * 60)"
        />
      </div>
    </div>

    <div v-if="recurrence?.mode === 'bimonthly'">
      <select
        v-if="withTimeMode"
        v-model="recurrence.params.timeMode"
        class="text-black mb-2"
      >
        <option v-for="mode in timeModes" :value="mode">
          {{ $t(`Recurrence${capitalize(mode)}`) }}
        </option>
      </select>
      <div
        class="grid items-center gap-1.5"
        style="grid-template-columns: auto 1fr"
      >
        <div class="px-2">{{ $t("RecurrenceLabelOnDay") }}</div>
        <klereo-slider
          v-model="recurrence.params.day"
          :min="1"
          :max="16"
          :precision="1"
        />

        <template v-if="recurrence.params.timeMode === 'fixedTime'">
          <div class="px-2">{{ $t("RecurrenceLabelOnTime") }}</div>
          <klereo-slider
            v-model="recurrence.params.hour"
            :min="0"
            :max="22"
            :precision="2"
            :format="(h) => formatTime(h * 60)"
          />
        </template>
      </div>
    </div>

    <div class="text-gray-300 mt-3" v-if="recurrence?.mode !== 'unique'">
      {{ pattern }}
    </div>
  </div>
</template>

<style scoped>
select {
  width: 100%;
  height: 34px;
  background: rgba(255, 255, 255, 0.17);
  @apply rounded-md;
  @apply px-2;
}
</style>
